import { PRO_SUBSCRIPTION_PERKS } from "@/constants";
import { usePermissions } from "@/features/Permissions/hooks/usePermissions";
import { UpsellModal } from "@/features/Subscriptions/components/UpsellModal/UpsellModal";
import { Illustrations } from "@/illustrations";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { v2 } from "@pufflig/prompt-studio-types";
import { EmptyState, Icon } from "@pufflig/prompt-studio-ui";
import { ErrorBoundary } from "@sentry/react";
import { format } from "date-fns";
import { Suspense, useState } from "react";
import { useDeployment } from "../hooks/useDeployments";
import { useWorkspaceKeys } from "../hooks/useWorkspaceKeys";
import { deploymentSchemaToPreview } from "../utils/deploymentSchemaToPreview";
import { CodeSnippet } from "./CodeSnippet/CodeSnippet";
import { DeploymentEditorDrawer } from "./DeploymentEditor/DeploymentEditor";
import { SchemaPreview } from "./SchemaPreview/SchemaPreview";
import { SchemaSection } from "./SchemaSection/SchemaSection";
import { SecretField } from "./SecretField/SecretField";

interface DeploymentDrawerProps {
  workspaceId: string;
  promptbookId: string;
  isOpen: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

export const DeploymentDrawer = (props: DeploymentDrawerProps) => {
  const { readOnly, promptbookId, workspaceId, isOpen, onClose } = props;

  const toast = useToast();
  const [isDeploymentEditorOpen, setDeploymentEditorOpen] = useState(false);
  const deploymentUpsellModalState = useDisclosure();
  const { data: deployment } = useDeployment(promptbookId);
  const { data: permissions } = usePermissions();
  const { canDeploy } = permissions || {};

  const { data: workspaceKeys } = useWorkspaceKeys(workspaceId);
  const workspaceKey = workspaceKeys?.[0]?.key;
  const [isWorkspaceKeyVisible, setIsWorkspaceKeyVisible] = useState(false);

  const deploymentState = deployment?.runbook as unknown as v2.RunState;
  const { requestSchema, responseSchema } = deploymentSchemaToPreview(deploymentState, deployment?.schema as any);

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Text textStyle="heading/2">API</Text>
          </DrawerHeader>
          <DrawerBody key={promptbookId}>
            <ErrorBoundary fallback={<p>Something went wrong</p>}>
              <Stack direction="column" gap="4" marginBottom="spacing-xl">
                <SecretField
                  workspaceKey={workspaceKey || ""}
                  onToggleVisible={(isVisible) => {
                    setIsWorkspaceKeyVisible(isVisible);
                  }}
                ></SecretField>
                {deployment && (
                  <Flex direction="column" gap="0.5">
                    <Text textStyle="label/S/regular" color="foreground.secondary">
                      Deployment Id
                    </Text>
                    <Tooltip label="Click to copy deployment id">
                      <Box
                        display="flex"
                        alignItems="center"
                        cursor="pointer"
                        width="100%"
                        border="2px solid"
                        borderColor={"border.secondary"}
                        paddingY="0.4rem"
                        paddingX="2"
                        borderRadius="8px"
                        onClick={() => {
                          navigator.clipboard.writeText(deployment.deployment_id);
                          toast({
                            position: "top",
                            title: "Deployment id copied to clipboard",
                            status: "success",
                            duration: 1000,
                            size: "sm",
                          });
                        }}
                      >
                        <Text textStyle="label/S/regular">{deployment.deployment_id}</Text>
                      </Box>
                    </Tooltip>
                    <Text textStyle="label/S/regular" color="foreground.secondary">
                      {format(deployment?.created_at, "yyyy.MM.dd HH:mm:ss")}
                    </Text>
                  </Flex>
                )}
                {deployment && (
                  <Flex>
                    <Tooltip label="Deploy this promptbook via our API">
                      <Button
                        size="sm"
                        isDisabled={!promptbookId || !workspaceKey || readOnly || !canDeploy}
                        variant="primary"
                        leftIcon={<Icon name="export-1" />}
                        onClick={() => {
                          setDeploymentEditorOpen(true);
                        }}
                      >
                        Deploy Promptbook
                      </Button>
                    </Tooltip>
                  </Flex>
                )}
                <Stack
                  sx={{
                    ":empty": {
                      display: "none",
                    },
                  }}
                >
                  {!deployment && (
                    <EmptyState
                      showBackground
                      title="You haven't deployed this promptbook"
                      description="Deploy Promptbooks to make them available via our API"
                      Illustration={<Illustrations.StartupDesktop height="10rem" width="10rem" />}
                      Action={
                        <Button
                          size="sm"
                          variant="primary"
                          leftIcon={<Icon name="export-1" />}
                          onClick={() => {
                            canDeploy ? setDeploymentEditorOpen(true) : deploymentUpsellModalState.onOpen();
                          }}
                        >
                          Deploy Promptbook
                        </Button>
                      }
                    />
                  )}
                </Stack>
                {deployment && workspaceKey && (
                  <>
                    <CodeSnippet
                      deploymentId={deployment.deployment_id}
                      workspaceKey={isWorkspaceKeyVisible ? workspaceKey : "<WORKSPACE_KEY>"}
                      requestSchema={requestSchema}
                      responseSchema={responseSchema}
                    />
                    <SchemaSection
                      readOnly
                      title="Request Schema"
                      description="The values you must pass to the placeholders of your promptbook."
                      buttonText="Add input field"
                      menuGroupText="Select input"
                    >
                      <SchemaPreview readOnly fields={requestSchema} headers={["Field", "", "", "Type"]} />
                    </SchemaSection>
                    <SchemaSection
                      readOnly
                      title="Response Schema"
                      description="The results of your promptbook that want to receive as part of the response body."
                      buttonText="Add output field"
                    >
                      <SchemaPreview readOnly fields={responseSchema} headers={["Field", "", "", "Type"]} />
                    </SchemaSection>
                  </>
                )}
              </Stack>
            </ErrorBoundary>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <UpsellModal
        title="Deploy Promptbooks"
        descriptions={["Upgrade your Prompt Studio account to deploy your Promptbooks via our API.", "Plus, enjoy..."]}
        features={PRO_SUBSCRIPTION_PERKS}
        Image={
          <Suspense>
            <Illustrations.ImageAddWorkspace />
          </Suspense>
        }
        isOpen={deploymentUpsellModalState.isOpen}
        onClose={deploymentUpsellModalState.onClose}
      />
      {isDeploymentEditorOpen && (
        <DeploymentEditorDrawer
          isOpen={isDeploymentEditorOpen}
          promptbookId={promptbookId}
          onClose={() => {
            setDeploymentEditorOpen(false);
          }}
        />
      )}
    </>
  );
};
